import { Route } from '@angular/router';

import { PermissionGuard } from '../../../+authenticated/permission-guard.service';
import { IntegrationAppGuard } from '../../../reducers/orm/integration-apps/integration-app.guard';
import {
  integrationAppOptions,
  IntegrationAppTitle,
} from '../../../reducers/orm/integration-apps/integration-app.model';

/**
 * Keys of `integrationAppOptions` whose entry has a `requestModal`. Constrains
 * `GENERIC_REQUEST_INTEGRATIONS` so misconfigured entries fail at compile time
 * rather than at module load.
 */
type RequestModalAppTitle = {
  [K in keyof typeof integrationAppOptions]: 'requestModal' extends keyof (typeof integrationAppOptions)[K] ? K : never;
}[keyof typeof integrationAppOptions];

/**
 * Integrations that use `IntegrationRequestComponent` for their request modal.
 *
 * Eligible: any integration whose request form is the standard 5-field contact set
 * (`mappingName`, `companyName`, `contactPersonName`, `phoneNumber` via `sb-phone-input`,
 * `contactPersonEmail`) and whose submit payload is
 * `{ api_app_id, name: mappingName, config: { ...rest } }`. An `importVat` entry on the
 * integration's `appConfigureConfig` adds the VAT toggle to the form and to `config`.
 *
 * Migration candidates (currently on their own `<integration>-request.component.ts`):
 *   - IntegrationAppTitle.MICE
 *   - IntegrationAppTitle.BORK
 *   - IntegrationAppTitle.CAOAPI
 *   - IntegrationAppTitle.JIXBEE
 *   - IntegrationAppTitle.NEXTUP
 *
 * Not eligible (bespoke fields, tooltips, dynamic selects, or different submit shapes):
 * Datafox, Formitable, Gastrofix, Guestplan, Mplus, Oneteam, Zenchef.
 */
const GENERIC_REQUEST_INTEGRATIONS: RequestModalAppTitle[] = [
  IntegrationAppTitle.OPUS_WATCH,
  IntegrationAppTitle.APPRENDO,
  IntegrationAppTitle.BUTLAROO,
  IntegrationAppTitle.MONOTREE,
];

export const integrationRequestRoutes: Route[] = GENERIC_REQUEST_INTEGRATIONS.map((appTitle) => ({
  path: integrationAppOptions[appTitle].requestModal,
  loadComponent: () => import('./integration-request.component').then((m) => m.IntegrationRequestComponent),
  canActivate: [PermissionGuard, IntegrationAppGuard],
  outlet: 'modal',
  data: { appTitle },
}));
